import { EN_DISTANCE_UNIT_UPPERCASE } from '@@sapporo/constant/enum/EN_DISTANCE_UNIT';
import { LeisureBrandHelper } from '@@sapporo/leisure/brand/LeisureBrandHelper';
import { ITicketPosition } from '@@sapporo/ticket/interface/ITicketPosition';
import qs from 'qs';
import { length, propOr } from 'ramda';
import { useCallback } from 'react';

import { Kinesis } from '../../services/TrackerService';
import { roundTo } from '..';
import { ITicketV3 } from '@/models/Leisure/interfaces/payloads/ILeisureProductsPayload';
import { ITicket } from '@yanolja-rnd/cx-joy-sdm';

/**
 * 지역과 카테고리 인터페이스를 사용해 상세 페이지 주소를 생성한다.
 * @param area 지역 코드
 * @param mediumcat 상위 카테고리 코드
 * @param smallcat 하위 카테고리 코드
 */
export const getLeisureHref = (area: number | null, mediumcat?: number | null, smallcat?: number): string => {
  const query = {
    area,
    mediumcat,
    smallcat,
  };
  return `/leisure/list?${qs.stringify(query)}`;
};

/**
 * 레저 상세 페이지 주소를 생성한다.
 * @param item 상세 주소를 생성할 레저 인터페이스
 */
export const getLeisureDetailHref = (item: ITicket | ITicketV3): string => {
  return `/leisure/${item.no}`;
};

/**
 * 티켓의 거리와 단위를 문자열로 변환해 반환한다.
 * @param distanceInfo 티켓 아이템의 `position.distance`
 */
export const formatDistance = (distanceInfo: ITicketPosition['distance']): string => {
  if (!distanceInfo) {
    return '';
  }
  if (distanceInfo.unit === EN_DISTANCE_UNIT_UPPERCASE.KILOMETER) {
    return `${roundTo(1, distanceInfo.amount)}km`;
  }
  return `${Math.round(distanceInfo.amount)}m`;
};

export interface IBadgesInfo {
  hotDeal: string | null;
  todayUse: string | null;
  fullRefund: string | null;
  specialPrice: string | null;
  leisureEvent: string | null;
}

/**
 * 핫딜, 당일사용가능, 미사용 전액환불 배지의 텍스트만 추출해 반환한다.
 * @param item 추출할 배지를 가진 레저티켓 인터페이스.
 */
export const formatBadgesInfo = (item: ITicket | ITicketV3): IBadgesInfo => {
  const hasTodayUsable = length(propOr([], 'todayUsable', item.badgesInfo)) > 0;
  const hasFullRefund = length(propOr([], 'fullRefund', item.badgesInfo)) > 0;
  const hasSpecialPrice = length(propOr([], 'lowestPrice', item.badgesInfo)) > 0;

  const hotDeal = item.modifiers.hotDealLv1?.title || null;
  const todayUse = hasTodayUsable ? item.badgesInfo.todayUsable[0].title : null;
  const fullRefund = hasFullRefund ? item.badgesInfo.fullRefund[0].title : null;
  const specialPrice = hasSpecialPrice ? item.badgesInfo.lowestPrice[0].title : null;
  const leisureEvent = item.eventBadges?.map(({ title }) => title).join(',') ?? null;

  return {
    hotDeal,
    todayUse,
    fullRefund,
    specialPrice,
    leisureEvent,
  };
};

/**
 * 레저 서브홈의 재시도 버튼 클릭 시 사용할 콜백을 반환한다.
 * @param leisureHomeSection 재시도 버튼을 포함한 섹션명. `custom.$leisureHomeSection`을 사용해야 한다.
 * @returns `useCallback()`으로 감싼 추적 함수.
 */
export const useLeisureHomeRetryTracker = (leisureHomeSection: string): (() => Promise<void>) => {
  return useCallback(
    () =>
      Kinesis.click('LeisureHome', {
        leisureHomeSection,
        object: 'retryBtn',
      }),
    [],
  );
};

/**
 * 레저 서브홈의 더보기(페이지네이션) 버튼 클릭 시 사용할 콜백 두 개를 튜플로 반환한다.
 * @param objectContainer 더보기 버튼을 포함한 섹션명. `objectContainer`를 사용해야 한다.
 * @returns `useCallback()`으로 감싼 추적 함수 두 개를 요소로 가진 튜플. `[0]`은 `backward`, `[1]`은 `forward`.
 */
export const useModulePaginationTracker: (
  objectContainer: string,
) => [React.MouseEventHandler, React.MouseEventHandler] = (objectContainer: string) => {
  const trackPrev = useCallback(
    () =>
      Kinesis.click('LeisureHome', {
        objectContainer,
        object: 'seeMoreBtn',
        directionBtn: 'backward',
      }),
    [],
  );

  const trackNext = useCallback(
    () =>
      Kinesis.click('LeisureHome', {
        objectContainer,
        object: 'seeMoreBtn',
        directionBtn: 'forward',
      }),
    [],
  );

  return [trackPrev, trackNext];
};

// 레저 브랜드관
export const logBrandBadge: Record<LeisureBrandHelper.EN_BADGE_TYPE, string> = {
  FIRST_COME_FIRST_SERVED: 'priorSale',
  LIMITED_PERIOD: 'limitedSale',
  EXCLUSIVE: 'exclusiveSale',
};
